import type { Identifier } from "ra-core";

// The resolved identity of a Next-Steps assignee. Internal = a teammate (sales),
// contact = a CRM contact, external = an attendee from outside the company we hold no
// record for, unresolved = the review UI must ask the user to pick.
//
// contact and external are the same answer to "is this ours to do" (no) and differ only
// in whether we can name the person. Keeping them apart matters because only one of them
// can offer a link to a record.
export type AssigneeBinding =
  | { kind: "sales"; sales_id: Identifier }
  | { kind: "contact"; contact_id: Identifier }
  | { kind: "external" }
  | { kind: "unresolved" };

// The minimal task shape these helpers read/write. Any other fields (text, due_date,
// collaboration_id, …) are preserved untouched when a binding is applied.
export type AssigneeTask = {
  assignee_name?: string | null;
  sales_id?: Identifier | null;
  contact_id?: Identifier | null;
  is_external?: boolean;
};

export type AssigneeRow = {
  name: string;
  count: number;
  binding: AssigneeBinding;
};

// Whose task is this? Read is_external FIRST, not contact_id: a draft can now carry a
// contact while still being the team's own work, because contact_id means "about this
// person" here exactly as it does on a real task. Only the flag says "theirs, not ours".
// With the flag set, contact_id tells a customer we hold a record for from an outside
// guest we do not. LCATOMICZO-31.
//
// The external check also has to sit above the sales one: an external draft keeps a
// sales_id as its internal follow-up owner, so reading sales_id first would call it
// internal work.
export function bindingOf(task: AssigneeTask): AssigneeBinding {
  if (task.is_external) {
    return task.contact_id != null
      ? { kind: "contact", contact_id: task.contact_id }
      : { kind: "external" };
  }
  if (task.sales_id != null) {
    return { kind: "sales", sales_id: task.sales_id };
  }
  return { kind: "unresolved" };
}

// One row per distinct assignee name (first-appearance order), with how many tasks share
// it and the binding taken from the group's first task (they share a binding once resolved).
export function deriveAssigneeRows(tasks: AssigneeTask[]): AssigneeRow[] {
  const order: string[] = [];
  const groups = new Map<string, { count: number; first: AssigneeTask }>();
  for (const t of tasks ?? []) {
    const name = (t.assignee_name ?? "").trim();
    if (!name) continue;
    const existing = groups.get(name);
    if (existing) {
      existing.count++;
    } else {
      groups.set(name, { count: 1, first: t });
      order.push(name);
    }
  }
  return order.map((name) => {
    const g = groups.get(name)!;
    return { name, count: g.count, binding: bindingOf(g.first) };
  });
}

// Is this draft somebody else's to do? True for a customer we hold a record for and for
// an outside attendee we do not, which are the same answer to the only question that
// matters here. Such drafts are hidden from the review list, never materialized into real
// tasks, and never block processing.
//
// The flag is the WHOLE test. It used to be `contact_id != null || is_external`, because
// carrying a contact was once how "theirs" was recorded. Now contact_id means "about this
// person" (the same as on a real task), so an internal draft can carry one and testing it
// here would hide the team's own work. Every writer — applyBinding, the webhook's ingest,
// and the re-parse path — sets the flag whenever it sets the id, which is what makes the
// narrower test equivalent on existing data. LCATOMICZO-31, narrowing half of
// LCATOMICZO-34.
//
// Mirrored by tasks_remaining in zoom_meetings_summary; change both together.
export function isExternalDraft(task: AssigneeTask): boolean {
  return task.is_external === true;
}

// Actionable tasks (those with text) that still have no teammate (sales rep). Used to
// enforce "every task needs a teammate" at process time — NOT on plain Save.
// Anything owned by someone outside the company is exempt: hidden from the task list,
// never materialized, so it must not block processing. Requiring the row is what used to
// make an outside guest's commitment demand a teammate before the meeting could be
// processed, a question with no correct answer.
//
// Exemption is the flag alone, for the reason isExternalDraft gives: a draft that merely
// names the contact it is about is the team's own work and still needs an owner.
export function tasksMissingSales<
  T extends AssigneeTask & { text?: string | null },
>(tasks: T[]): T[] {
  return (tasks ?? []).filter(
    (t) => (t.text ?? "").trim() !== "" && !t.is_external && t.sales_id == null,
  );
}

// Apply a binding to EVERY task with the given assignee name, leaving all other tasks and
// all other fields untouched. sales → internal; contact → external (owner kept, else the
// current user); unresolved → cleared.
//
// Rebinding does NOT clear contact_id any more, because the id is no longer the ownership:
// it says who the task is about. "Ada said she'd do this, actually Elijah will, and it is
// still about Ada" is the right outcome, so only the flag changes. The one exception is the
// external branch below, which clears it on purpose. LCATOMICZO-31.
export function applyBinding<T extends AssigneeTask>(
  tasks: T[],
  name: string,
  binding: AssigneeBinding,
  ownerId?: Identifier,
): T[] {
  const target = name.trim();
  return (tasks ?? []).map((t) => {
    if ((t.assignee_name ?? "").trim() !== target) return t;
    if (binding.kind === "sales") {
      return {
        ...t,
        sales_id: binding.sales_id,
        is_external: false,
      };
    }
    if (binding.kind === "contact") {
      return {
        ...t,
        contact_id: binding.contact_id,
        is_external: true,
        // sales_id on an external item is the internal follow-up owner — keep an existing
        // one, else fall back to the current user.
        sales_id: t.sales_id ?? ownerId ?? null,
      };
    }
    if (binding.kind === "external") {
      // Same as a contact binding minus the record: theirs to do, not ours, and there is
      // no contact row to point at. Deliberately does not create one — inventing a CRM
      // contact for every demo attendee is how the junk-records problem starts.
      //
      // This is the one branch that still clears contact_id: "Outside guest" asserts we
      // hold no record for this person, so a leftover id would make bindingOf read the row
      // back as a contact binding and show a name the reviewer did not pick.
      return {
        ...t,
        contact_id: null,
        is_external: true,
        sales_id: t.sales_id ?? ownerId ?? null,
      };
    }
    return { ...t, sales_id: null, is_external: false };
  });
}
